import { FeedbackType, PlacementId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { InlineFeedbackResponse } from "components/feedback/inline-feedback";
import { trpc } from "components/rpc/trpc";
import { useCallback, useState } from "react";
import { handleEvent } from "util/user-events";

export const AI_SUMMARY_FEEDBACK_OPTIONS = [
    "Inaccurate information",
    "Missing important details",
    "Too long",
    "Too short",
    "Not relevant",
];

export const useClientAiSummary = (clientId: PlacementId) => {
    const trpcClient = trpc.useTRPC();
    const [feedbackGiven, setFeedbackGiven] = useState<InlineFeedbackResponse>(null);
    const [showRemoveButton, setShowRemoveButton] = useState(false);

    const {
        data: summary,
        isLoading: isQueryLoading,
        error: queryError,
        refetch: refetchSummary,
    } = useQuery({
        ...trpcClient.clientAiSummary.find.queryOptions({ clientId }),
    });

    const generateSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.generate.mutationOptions(),
        onSuccess: () => {
            setFeedbackGiven(null);
            setShowRemoveButton(false);
            refetchSummary();
        },
    });

    const submitFeedbackMutation = useMutation({
        ...trpcClient.clientAiSummary.submitFeedback.mutationOptions(),
    });

    const updateTextMutation = useMutation({
        ...trpcClient.clientAiSummary.updateText.mutationOptions(),
        onSuccess: () => {
            refetchSummary();
        },
    });

    const removeSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.removeSummary.mutationOptions(),
        onSuccess: () => {
            setFeedbackGiven(null);
            setShowRemoveButton(false);
            refetchSummary();
        },
    });

    const updateText = useCallback(
        async (newText: string): Promise<boolean> => {
            if (!summary) return false;
            try {
                await updateTextMutation.mutateAsync({
                    summaryId: summary.id,
                    text: newText,
                });
                return true;
            } catch {
                return false;
            }
        },
        [summary, updateTextMutation],
    );

    const generate = useCallback(() => {
        handleEvent("ai-summary-generate", {
            clientId,
            isRegenerate: !!summary,
        });
        generateSummaryMutation.mutate({
            clientId,
        });
    }, [clientId, generateSummaryMutation, summary]);

    const submitPositiveFeedback = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("positive");
        submitFeedbackMutation.mutate({
            summaryId: summary.id,
            feedbackType: FeedbackType.Positive,
        });
    }, [summary, submitFeedbackMutation]);

    const startNegativeFeedback = useCallback(() => {
        if (!summary) return;
        setFeedbackGiven("negative");
        setShowRemoveButton(true);
    }, [summary]);

    const submitNegativeFeedback = useCallback(
        (combinedText: string | null) => {
            if (!summary) return;
            submitFeedbackMutation.mutate({
                summaryId: summary.id,
                feedbackType: FeedbackType.Negative,
                feedbackText: combinedText || undefined,
            });
        },
        [summary, submitFeedbackMutation],
    );

    const removeSummary = useCallback(() => {
        if (!summary) return;
        removeSummaryMutation.mutate({
            summaryId: summary.id,
        });
    }, [summary, removeSummaryMutation]);

    return {
        summary: summary ?? null,
        isLoading: isQueryLoading || generateSummaryMutation.isPending,
        isQueryLoading,
        isGenerating: generateSummaryMutation.isPending,
        isRemoving: removeSummaryMutation.isPending,
        isSubmittingFeedback: submitFeedbackMutation.isPending,
        error:
            queryError?.message ||
            generateSummaryMutation.error?.message ||
            updateTextMutation.error?.message,
        feedbackGiven,
        showRemoveButton,
        generate,
        updateText,
        submitPositiveFeedback,
        startNegativeFeedback,
        submitNegativeFeedback,
        removeSummary,
    };
};

export type ClientAiSummaryState = ReturnType<typeof useClientAiSummary>;
